import { Injectable } from '@angular/core';
import { AWTPiiKind } from '@aria/webjs-sdk';

/**
 * Parameters that can be stored with instrumentation events.
 */
export const enum InstrumentationParam {
    Action = 'Action',
    ActiveDaysLeft = 'ActiveDaysLeft',
    ActivityCollaboration = 'ActivityCollaboration',
    ActivityCommentsCount = 'ActivityCommentsCount',
    ActivityCountSinceLastOpened = 'ActivityCountSinceLastOpened',
    ActivityDisplayed = 'ActivityDisplayed',
    ActivityDistribution = 'ActivityDistribution',
    ActivityDuration = 'ActivityDuration',
    ActivityEditsCount = 'ActivityEditsCount',
    ActivityFilters = 'ActivityFilters',
    ActivityFromMruAndVroomMatch = 'ActivityFromMruAndVroomMatch',
    ActivityFromMruTimestamp = 'ActivityFromMruTimestamp',
    ActivityFromMruType = 'ActivityFromMruType',
    ActivityFromVroomTimestamp = 'ActivityFromVroomTimestamp',
    ActivityFromVroomType = 'ActivityFromVroomType',
    ActivityMentionsCount = 'ActivityMentionsCount',
    ActivitySessionId = 'ActivitySessionId',
    ActivityTotalCount = 'ActivityTotalCount',
    ActualSize = 'ActualSize',
    ApiLatency = 'ApiLatency', // This is meant to be the server-side latency for an API. Some APIs expose this in a response header.
    AppFilters = 'AppFilters',
    AppSecurityLevel = 'AppSecurityLevel',
    Area = 'Area',
    BatchId = 'BatchId',
    BatchSize = 'BatchSize',
    BEServer = 'BEServer',
    CachedPage = 'CachedPage',
    CalendarEventsDaily = 'CalendarEventsDaily',
    CalendarEventsMoreLink = 'CalendarEventsMoreLink',
    CalendarEventsType = 'CalendarEventsType',
    CalendarIsOrganizer = 'CalendarIsOrganizer',
    CalendarWeekday = 'CalendarWeekday',
    ClickCount = 'ClickCount',
    ClickDestination = 'ClickDestination',
    ClickTime = 'ClickTime',
    BundleBuildDate = 'BundleBuildDate',
    BundleBuildId = 'BundleBuildId',
    Consumer = 'Consumer',
    ConsumerSubscriptionType = 'ConsumerSubscriptionType',
    DateFilters = 'DateFilters',
    DaysExpired = 'DaysExpired',
    DebugInfo = 'DebugInfo',
    DevicePlatform = 'DevicePlatform',
    DimeExperience = 'DimeExperience',
    DiskWrite = 'DiskWrite',
    Distribution = 'Distribution',
    DocumentId = 'DocumentId',
    DocumentType = 'DocumentType',
    DocumentPreload = 'DocumentPreload',
    DocumentHoverClick = 'DocumentHoverClick',
    ElementId = 'ElementId',
    Error = 'Error',
    ErrorCount = 'ErrorCount',
    EventName = 'EventName',
    ExpiredSkuType = 'ExpiredSkuType',
    FileFormat = 'FileFormat',
    Filename = 'Filename',
    FirstRunFurthestCompleted = 'FirstRunFurthestCompleted',
    FirstRunPreviousStep = 'FirstRunPreviousStep',
    FirstRunStepsCompleted = 'FirstRunStepsCompleted',
    FirstRunTotalSteps = 'FirstRunTotalSteps',
    FirstRunType = 'FirstRunType',
    FirstRunUserDismissedCallout = 'FirstRunUserDismissedCallout',
    FirstRunVersion = 'FirstRunVersion',
    Flights = 'Flights', // Will override the default set of flights. Don't use this when recording individual events since we want all events to have the same set of flights.
    FromServiceWorkerCache = 'FromServiceWorkerCache',
    HasTextFilter = 'HasTextFilter',
    HostSessionId = 'HostSessionId',
    HttpCode = 'HttpCode',
    ImpressionId = 'ImpressionId',
    InactivityDuration = 'InactivityDuration',
    Increment = 'Increment',
    Index = 'Index',
    InitialPage = 'InitialPage',
    InstallableApp = 'InstallableApp',
    InstallerType = 'InstallerType',
    IsNewDistribution = 'IsNewDistribution',
    IsSharedDistribution = 'IsSharedDistribution',
    IsOnPremOrVanity = 'IsOnPremOrVanity',
    IsEmailAttachment = 'IsEmailAttachment',
    ItemCategory = 'ItemCategory',
    ItemCount = 'ItemCount',
    ItemId = 'ItemId',
    ItemIsNew = 'ItemIsNew',
    ItemIsShared = 'ItemIsShared',
    Latency = 'Latency',
    LearnMoreTarget = 'LearnMoreTarget',
    Length = 'Length',
    ListId = 'ListId',
    LoggerId = 'LoggerId',
    MemoryProfileBreakdown = 'MemoryProfileBreakdown',
    MetricName = 'MetricName',
    MruDuration = 'MruDuration',
    MruPlusPlusV2MetaDataDistribution = 'MruPlusPlusV2MetaDataDistribution',
    MsNewParam = 'MsNewParam',
    NetworkType = 'NetworkType',
    ResourceIdDistribution = 'ResourceIdDistribution',
    OfficeFeedCategoryDistribution = 'OfficeFeedCategoryDistribution',
    OfficeFeedUrl = 'OfficeFeedUrl',
    OfficeFeedAuthenticateHeader = 'OfficeFeedAuthenticateHeader',
    OLSToken = 'OLSToken',
    Page = 'Page',
    CurrentPageLayout = 'CurrentPageLayout',
    PartnerCorrelationId = 'PartnerCorrelationId',
    PartnerGeo = 'PartnerGeo',
    PeakPrivateCommit = 'PeakPrivateCommit',
    PeakTotalWorkingSet = 'PeakTotalWorkingSet',
    Pinned = 'Pinned',
    PrelaunchMode = 'PrelaunchMode', // Applicable only to PWA where app is prelaunched but user hasn't interacted with it
    PrivateCommit = 'PrivateCommit',
    PrivateWorkingSet = 'PrivateWorkingSet',
    ProcessCpu = 'ProcessCpu',
    ProcessorCount = 'ProcessorCount',
    Provisioning = 'Provisioning',
    RecentInflationCount = 'RecentInflationCount',
    RecentInflationLatency = 'RecentInflationLatency',
    RecommendedInflationCount = 'RecommendedInflationCount',
    Referral = 'Referral',
    ReferralPlatform = 'ReferralPlatform',
    ReferralType = 'ReferralType',
    RelativeIndex = 'RelativeIndex',
    RenderSize = 'RenderSize',
    ReportingEnabled = 'ReportingEnabled',
    RequestId = 'RequestId',
    ResourceId = 'ResourceId',
    ResourceUrl = 'ResourceUrl',
    Retry = 'Retry',
    Ring = 'Ring',
    SearchCharacterCount = 'SearchCharacterCount',
    SearchItemCount = 'SearchItemCount',
    SearchProviderType = 'SearchProviderType',
    SearchQueryId = 'SearchQueryId', // Only used for consumer search.
    SearchResultType = 'SearchResultType',
    SearchSessionId = 'SearchSessionId', // Only used for consumer search.
    SearchTargetId = 'SearchTargetId', // Only used for consumer search.
    SearchWordCount = 'SearchWordCount',
    ServerBuildDate = 'ServerBuildDate',
    ServerBuildId = 'ServerBuildId',
    ServiceWorkerProcessingLatency = 'ServiceWorkerProcessingLatency',
    ServiceWorkerState = 'ServiceWorkerState',
    ShareControlLatency = 'ShareControlLatency',
    ShareControlMode = 'ShareControlMode',
    SharedFilters = 'SharedFilters',
    SharedWithMeVersion = 'SharedWithMeVersion',
    SharePointDuration = 'SharePointDuration',
    SharePointFollowingSitesCount = 'SharePointFollowingSitesCount',
    SharePointFollowingAndFrequentCount = 'SharePointFollowingAndFrequentCount',
    SharePointFrequentSitesCount = 'SharePointFrequentSitesCount',
    SharePointSiteLists = 'SharePointSiteLists',
    ShellBootAfterStartup = 'ShellBootAfterStartup',
    SkuType = 'SkuType',
    State = 'State',
    StorageType = 'StorageType',
    SuccessCount = 'SuccessCount',
    SuggestedActionType = 'SuggestedActionType',
    SuggestedActionsDistribution = 'SuggestedActionsDistribution',
    SystemCpu = 'SystemCpu',
    SystemMemoryTotal = 'SystemMemoryTotal',
    SystemMemoryUsage = 'SystemMemoryUsage',
    TabState = 'TabState',
    TemplateType = 'TemplateType',
    TemplateId = 'TemplateId',
    TimeElapsed = 'TimeElapsed',
    TotalCommit = 'TotalCommit',
    TotalSize = 'TotalSize',
    TotalWorkingSet = 'TotalWorkingSet',
    UploadSessionId = 'UploadSessionId',
    UsingSharePointResourceId = 'UsingSharePointResourceId',
    VersionHistory = 'VersionHistory',
    WacEventName = 'WacEventName',
    WacHostEntry = 'WacHostEntry',
    MoveToFolderControlLatency = 'MoveToFolderControlLatency',
    IsCrossSiteMove = 'IsCrossSiteMove'
}

/**
 * All event names.
 */
export const enum KnownInstrumentationEvents {
    ActionsMenuItemClick = 'Actions_Menu_Item_Click',
    ActionsMenuToggle = 'Actions_Menu_Toggle',
    ActionsMenuReactToggle = 'Actions_Menu_React_Toggle',
    ActivitySessionFinished = 'Activity_Session_Finished',
    ActivitySessionStarted = 'Activity_Session_Started',
    AddToHomeScreenPromptImpressionMobile = 'Add_To_Home_Screen_Prompt_Mobile',
    AddToHomeScreenPromptImpressionDesktop = 'Add_To_Home_Screen_Prompt_Impression_Desktop',
    AllowFileSystemAccess = 'Allow_File_System_Access',
    AllTemplatesClick = 'All_Templates_Click',
    AppAddedToHomeScreen = 'App_Added_To_Home_Screen',
    AppAddedToHomeScreenMobile = 'App_Added_To_Home_Screen_Mobile',
    AppAddedToHomeScreenDesktop = 'App_Added_To_Home_Screen_Desktop',
    AppItemsImpression = 'App_Items_Impression',
    AnnouncementImpression = 'Announcement_Impression',
    AppError = 'App_Error',
    AppGalleryLearnMoreClick = 'AppGallery_LearnMoreClick',
    AppGalleryCardImpression = 'AppGallery_Card_Impression',
    AppGalleryClick = 'AppGallery_Click',
    AppGalleryNavClick = 'AppGallery_Nav_Click',
    AppGalleryPageView = 'AppGallery_PageView',
    AppInstalledStatus = 'App_Installed_Status',
    AppListImpression = 'AppList_Impression',
    AppServiceConnection = 'App_Service_Connection',
    AppTileRightClick = 'App_Tile_Right_Click',
    AppTilesImpression = 'App_Tiles_Impression',
    AppTilesReady = 'App_Tiles_Ready',
    AuthStateChanged = 'Auth_State_Changed',
    BulkLicensePreview = 'Bulk_License_Preview',
    BulkLicensePreviewRequest = 'Bulk_License_Preview_Request',
    CalendarClick = 'Calendar_Click',
    CalendarDayImpression = 'Calendar_Day_Impression',
    CalendarRequest = 'Calendar_Request',
    CAPPopup = 'CAP_Popup',
    CheckLoginRequest = 'Check_Login_Request',
    ClearSWCache = 'Clear_SW_Cache',
    ConsumerSkuLoaded = 'ConsumerSkuLoaded',
    ConsumerUpsellImpression = 'ConsumerUpsell_InstallImpression',
    ContentActionsMenuToggle = 'Content_Actions_Menu_Toggle',
    CreateCenterCreateTilesImpression = 'Create_Center_Create_Tiles_Impression',
    CreateCenterEnterpriseTemplatesImpression = 'Create_Center_Enterprise_Templates_Impression',
    CreateCenterTemplatesCollectionsImpression = 'Create_Center_Templates_Collections_Impression',
    CreateFromDocumentClick = 'Create_From_Document_Click',
    CreateFromDocumentClickResult = 'Create_From_Document_Click_Result',
    CreateLinkClick = 'Create_Link_Click',
    CreateLinkClickResult = 'Create_Link_Click_Result',
    CreateMenuImpression = 'Create_Menu_Impression',
    CreateMenuToggle = 'Create_Menu_Toggle',
    CreateModalImpression = 'Create_Modal_Impression',
    CreateNewRedirectSuccess = 'Create_New_Redirect_Success',
    CreateNewRedirectFail = 'Create_New_Redirect_Fail',
    CreateOnHomeCreateTilesImpression = 'Create_On_Home_Create_Tiles_Impression',
    CreatePanelImpression = 'Create_Panel_Impression',
    CsrfTokenError = 'Csrf_Token_Error',
    CustomStartDeprecationModalImpression = 'CustomStart_Deprecation_Modal_Impression',
    DimeCompleted = 'Dime_Completed',
    DimeTransactionCompleted = 'DimeTransactionCompleted',
    DimeInitialize = 'Dime_Initialize',
    DimeLaunch = 'Dime_Launch',
    DimeLoadScript = 'Dime_LoadScript',
    DimePreload = 'Dime_Preload',
    DimeWarmup = 'Dime_Warmup',
    DocumentHover = 'Document_Hover',
    DocumentTitleRenameFile = 'Document_Title_Rename_File',
    DocumentTitleRenameFileError = 'Document_Title_Rename_File_Error',
    DTTShellContainerNotFound = 'Document_Title_Shell_Container_Not_Found',
    EdgeworthBulkCacheRead = 'EdgeworthBulkCacheRead',
    EdgeworthCacheDeleteError = 'Edgeworth_Cache_Delete_Error',
    EdgeworthCacheRead = 'EdgeworthCacheRead',
    EdgeworthCacheWrite = 'EdgeworthCacheWrite',
    EdgeworthDuplicateFilesDetected = 'EdgeworthDuplicateFilesDetected',
    EdgeworthRebuildError = 'EdgeworthRebuildError',
    EdgeworthCalloutImpression = 'Edgeworth_Callout_Impression',
    EdgeworthClick = 'Edgeworth_Click',
    EdgeworthFilterSelect = 'Edgeworth_Filter_Select',
    EdgeworthFullSyncRequest = 'EdgeworthFullSyncRequest',
    EdgeworthHideItemRequest = 'EdgeworthHideItemRequest',
    EdgeworthImpression = 'Edgeworth_Impression',
    EdgeworthParseError = 'Edgeworth_Parse_Error',
    EdgeworthPinItemRequest = 'EdgeworthPinItemRequest',
    EdgeworthRebuildRequired = 'EdgeworthRebuildRequired',
    EdgeworthRebuildCompleted = 'EdgeworthRebuildCompleted',
    EdgeworthSubstrateRecentRequest = 'EdgeworthSubstrateRecentRequest',
    EdgeworthSubstrateDeltaSyncPageRequest = 'EdgeworthSubstrateDeltaSyncPageRequest',
    EdgeworthTextFilterUsed = 'EdgeworthTextFilterUsed',
    EdgeworthUnhideItemRequest = 'EdgeworthUnhideItemRequest',
    EdgeworthUnpinItemRequest = 'EdgeworthUnpinItemRequest',
    EduThemesCalloutImpression = 'EduThemesCallout_Impression',
    ElementInViewport = 'Element_In_Viewport',
    EmbedBootstrapEnd = 'Embed_Bootstrap_End',
    ErrorDialogImpression = 'Error_Dialog_Impression',
    ExpandableListToggle = 'Expandable_List_Toggle',
    ExpandedWorkspacesRequest = 'Expanded_Workspaces_Request',
    ExploreAllYourAppsImpression = 'ExploreAllYourApps_Impression',
    FastSearchPageView = 'FastSearch_PageView',
    FeaturedDocumentsClick = 'FeaturedDocuments_Click',
    FeaturedDocumentsErrorImpression = 'FeaturedDocuments_Error_Impression',
    FeaturedDocumentsImpression = 'FeaturedDocuments_Impression',
    FeaturedDocumentsRequest = 'FeaturedDocuments_Request',
    FeaturedDocumentsToggle = 'FeaturedDocuments_Toggle',
    FeedbackOCVClick = 'Feedback_OCV_Click',
    FeedbackOCVDismissed = 'Feedback_OCV_Dismissed',
    FeedbackOCVError = 'Feedback_OCV_Error',
    FeedbackOCVSubmitted = 'Feedback_OCV_Submitted',
    FileVersionListFetch = 'File_Version_List_Fetch',
    FirstRunCompleted = 'First_Run_Completed',
    FirstRunImpression = 'First_Run_Impression',
    FirstRunSlideStats = 'First_Run_Slide_Stats',
    FirstSiteVisit = 'First_Site_Visit',
    FirstTimeMetric = 'First_Time_Metric',
    FluidContentError = 'FluidContent_Error',
    FluidContentCreateNew = 'FluidContent_CreateNew',
    FluidContentOpen = 'FluidContent_Open',
    FluidContentTimeout = 'FluidContent_Timeout',
    FluidFileInfoError = 'Fluid_FileInfo_Error',
    FluidFileLoadError = 'FluidFileLoad_Error',
    FluidFileLocationInfoError = 'FluidFile_LocationInfo_Error',
    FluidInformationProtectionError = 'Fluid_InformationProtection_Error',
    FluidShareControlError = 'Fluid_ShareControl_Error',
    FluidShareControlStart = 'Fluid_ShareControl_Start',
    FluidTokenFetch = 'Fluid_TokenFetch',
    FluidVersionHistoryDTTClick = 'Fluid_VersionHistoryDTTClick',
    FluidMobileUpsellImpression = 'Fluid_MobileUpsell_Impression',
    FluidMobileUpsellInstallClick = 'Fluid_MobileUpsell_Install_Click',
    FreNewCalloutsEvent = 'Fre_New_Callouts_Event',
    FreNewCalloutsImpression = 'Fre_New_Callouts_Impression',
    FocusContextError = 'Focus_Context_Error',
    GalleryCardImpression = 'Gallery_Card_Impression',
    GalleryNavClick = 'Gallery_Nav_Click',
    GenericDebugCache = 'Generic_Debug_Cache',
    GenericClick = 'Generic_Click',
    GenericEvent = 'Generic_Event',
    GenericImpression = 'Generic_Impression',
    GenericRequest = 'Generic_Request',
    GetClassNotebooksRequest = 'Get_ClassNotebooks_Request',
    GetDownloadUrlError = 'Get_DownloadUrl_Error',
    GetOneNoteSectionsRequest = 'Get_OneNote_Sections_Request',
    GetOneNotePageRequest = 'Get_OneNote_Page_Request',
    GetSiteTitleRequest = 'Get_Site_Title_Request',
    GetForm = 'Get_Form',
    GetUserReputationTier = 'Get_User_Reputation_Tier',
    EnterpriseTemplatesErrorImpression = 'Enterprise_Templates_Error_Impression',
    EnterpriseTemplatesImpression = 'Enterprise_Templates_Impression',
    EmbedSwGetTokenError = 'Embed_Sw_Get_Token_Error',
    InvalidMethod = 'Invalid_Method',
    FormsMembersThumbnailFetch = 'Forms_Members_Thumbnail_Fetch',
    FormsGroupThumbnailFetch = 'Forms_Group_Thumbnail_Fetch',
    FormsSharedUnpinRequest = 'Forms_Shared_UnpinRequest',
    FormsCopyRequest = 'Forms_Copy_Request',
    FormsGroupsRequest = 'Forms_Groups_Request',
    FormsFromGroupRequest = 'Forms_From_Group_Request',
    FormsResponses = 'Forms_Responses',
    FormsThemeRequest = 'Forms_Theme_Request',
    FormsDeleteRequest = 'Forms_Delete_Request',
    FormsFeatureCallout = 'Forms_Feature_Callout',
    GroupSideMenuMoveClick = 'Group_SideMenu_Move_Click',
    GroupFormsCreateRequest = 'Group_Forms_Create_Request',
    FormsGroupMembersRequest = 'Forms_Group_Members_Request',
    InitializeWopiPendingFailed = 'Initialize_Wopi_Pending_Failed',
    SharedFormsRequest = 'Shared_Forms_Request',
    MyFormsRequest = 'My_Forms_Request',
    RestoreFormRequest = 'Restore_Form_Request',
    RecycleFormRequest = 'Recycle_Form_Request',
    FormCreateFormFromTemplate = 'Form_Create_Form_From_Template',
    GraphBatchRequest = 'Graph_Batch_Request',
    GroupItemClick = 'Group_Item_Click',
    HelpSearchSuggestionRequest = 'Help_Search_Suggestion_Request',
    HistoryDisabled = 'History_Disabled',
    HomePageView = 'Home_PageView',
    ImpressionsSinceFirstVisit = 'Impressions_Since_First_Visit',
    InstallClick = 'Install_Click',
    InstallCustomError = 'Install_Custom_Error',
    InstallGenericError = 'Install_Generic_Error',
    InstallableAppsRequest = 'Installable_Apps_Request',
    InstalledAppsFetch = 'Installed_Apps_Fetch',
    InstalledAppsExFetch = 'Installed_Apps_Ex_Fetch',
    InstalledAppsExFetchDebug = 'Installed_Apps_Ex_Fetch_Debug',
    InstalledOfficeDetailsFetch = 'Installed_Office_Details_Fetch',
    InstallerRequest = 'Installer_Request',
    InstallScenario = 'Install_Scenario',
    LaunchByProtocolFailed = 'Launch_By_Protocol_Failed',
    LaunchFileExplorerFailed = 'Launch_File_Explorer_Failed',
    LaunchLocalApp = 'Launch_Local_App',
    LeftNavCalloutImpression = 'Left_Nav_Callout_Impression',
    LeftNavImpression = 'Left_Nav_Impression',
    LeftNavReady = 'LeftNav_Ready',
    LocalMruFetch = 'Local_Mru_Fetch',
    LocalStorageUnavailable = 'Local_Storage_Unavailable',
    M365AppsDefinitionError = 'M365_Apps_Definition_Error',
    M365AppsTabDefinitionError = 'M365_Apps_Tab_Definition_Error',
    MeetingObjectsNoArtifactsFoundError = 'Meeting_Objects_No_Artifacts_Found_Error',
    MeetNowButtonImpression = 'Meet_Now_Button_Impression',
    MemoryUsage = 'Memory_Usage',
    MetaTransformCapacityRequest = 'MeTA_Transform_Capacity_Request',
    MobileAppBannerImpression = 'Mobile_App_Banner_Impression',
    MobileView = 'Mobile_View',
    MruClick = 'Mru_Click',
    MruDelete = 'Mru_Delete',
    MruPathClick = 'Mru_Path_Click',
    MruUpdatePin = 'Mru_Update_Pin',
    MruImpression = 'Mru_Impression',
    MruRequest = 'Mru_Request',
    MruS2SRequest = 'Mru_S2S_Request',
    MruWrite = 'Mru_Write',
    MruWriteError = 'Mru_Write_Error',
    MruFileItemError = 'Mru_File_Item_Error',
    MsaTokenFetch = 'Msa_Token_Fetch',
    NavigateToTemplatesClick = 'Navigate_To_Templates_Click',
    NewConsumerNoContentImpression = 'New_Consumer_No_Content_Impression',
    NewPlaylistButtonClick = 'New_Playlist_Button_Click',
    NewUserShelfImpression = 'New_User_Shelf_Impression',
    NewScreenRecorderButtonClick = 'New_Screen_Recorder_Button_Click',
    NewVideoRecorderButtonClick = 'New_Video_Recorder_Button_Click',
    NewUserShelfClick = 'New_User_Shelf_Click',
    OfficeConsumerLicenseFetch = 'Office_Consumer_License_Fetch',
    OfficeFeedInstrumentation = 'OfficeFeedInstrumentation',
    OfficeFeedRequest = 'OfficeFeedRequest',
    OfficeFeedAction = 'OfficeFeedAction',
    OfficeFeedStreamThumbnailRequest = 'OfficeFeed_Stream_Thumbnail_Request',
    OfficeHomeStorageReadRequest = 'OfficeHomeStorage_Read_Request',
    OfficeHomeStorageWriteRequest = 'OfficeHomeStorage_Write_Request',
    OfficeInsightsSearchSuggestionRequest = 'OfficeInsights_Search_Telemetry_Request',
    OfficeLicensesFetch = 'Office_Licenses_Fetch',
    OneDriveGraphCancelUploadSessionRequest = 'OneDrive_Graph_Cancel_Upload_Session_Request',
    OneDriveGraphCreateFileRequest = 'OneDrive_Graph_Create_File_Request',
    OneDriveGraphCreateUploadSessionRequest = 'OneDrive_Graph_Create_Upload_Session_Request',
    OneDriveGraphItemRequest = 'OneDrive_Graph_Item_Request',
    OneDriveGraphItemError = 'OneDrive_Graph_Item_Error',
    OneDriveGraphItemWithWacDataAndThumbRequest = 'OneDrive_Graph_Item_With_Wac_Data_And_Thumb_Request',
    OneDriveGraphItemWithWacDataRequest = 'OneDrive_Graph_Item_With_Wac_Data_Request',
    OneDriveGraphSharesRequest = 'OneDrive_Graph_Shares_Request',
    OneDriveGraphSharesWithWacDataRequest = 'OneDrive_Graph_Shares_With_Wac_Data_Request',
    OneDriveGraphBatchSharesRequest = 'OneDrive_Graph_Batch_Shares_Request',
    OneDriveGraphThumbnailRequest = 'OneDrive_Graph_Thumbnail_Request',
    OneDriveGraphThumbnailStringRequest = 'OneDrive_Graph_Thumbnail_String_Request',
    OneDriveGraphPeoplePictureRequest = 'OneDrive_Graph_People_Picture_Request',
    OneDriveGraphBatchPeoplePicturesRequest = 'OneDrive_Graph_Batch_People_Pictures_Request',
    OneDriveGraphUploadFileRequest = 'OneDrive_Graph_Upload_File_Request',
    OneDriveGraphUploadFragmentRequest = 'OneDrive_Graph_Upload_Fragment_Request',
    OneDriveGraphUploadStatusRequest = 'OneDrive_Graph_Upload_Status_Request',
    OneDrivePlacesBadUrl = 'OneDrive_Places_Bad_Url',
    OneDrivePlacesClick = 'OneDrive_Places_Click',
    OneDrivePlacesImpression = 'OneDrive_Places_Impression',
    OneDriveUserRootUrl = 'OneDrive_User_RootUrl',
    ODSPProvisioningStatus = 'ODSP_Provisioning_Status',
    ODSPProvisioningFinish = 'ODSP_Provisioning_Finish',
    ODSPProvisioningGiveUp = 'ODSP_Provisioning_GiveUp',
    ODSPInstaProvisionedRedirect = 'ODSP_Insta_Provisioned_Redirect',
    OneNoteNotebookCreateLinkClick = 'OneNote_Notebook_Create_Link_Click',
    OneNotePageCreateLinkClick = 'OneNote_Page_Create_Link_Click',
    OneNoteGraphCreateNoteBookRequest = 'OneNote_Graph_Create_NoteBook_Request',
    OneNoteGraphGetNotebooksRequest = 'OneNote_Graph_Get_NoteBooks_Request',
    OtelReady = 'OTEL_Ready',
    OtelInitializationError = 'OTEL_Initialization_Error',
    GroupsImpression = 'Groups_Impression',
    PerformanceDump = 'Performance_Dump',
    PerformanceMarkFailure = 'Performance_Mark_Failure',
    PeopleCardImpression = 'People_Card_Impression',
    PeopleCardInitialization = 'People_Card_Initialization',
    PinToTaskbar = 'Pin_To_Taskbar',
    PrimaryTokenFetchRequest = 'Primary_Token_Fetch_Request',
    PwaBootstrapEnd = 'Pwa_Bootstrap_End',
    PwaOnActivated = 'Pwa_On_Activated',
    PwaNavigationFailed = 'Pwa_Navigation_Failed',
    PwaJumpListItemClick = 'Pwa_Jump_List_Item_Click',
    PwaJumpList = 'Pwa_Jump_List',
    PwaSwHandleRegistrationError = 'Pwa_Sw_Handle_Registration_Error',
    PwaSwGetTokenError = 'Pwa_Sw_Get_Token_Error',
    RecommendedCalloutImpression = 'Recommended_Callout_Impression',
    RecommendedClick = 'Recommended_Click',
    RecommendedDelete = 'Recommended_Delete_Request',
    RecommendedImpression = 'Recommended_Impression',
    RecommendedInflationComplete = 'Recommended_Inflation_Complete',
    RecommendedNavClick = 'Recommended_Nav_Click',
    RecommendedRequest = 'Recommended_Request',
    RefreshAccessTokenRequest = 'Refresh_Access_Token_Request',
    ReturningUserCalloutsImpression = 'Returning_User_Callouts_Impression',
    SearchAutoSuggestionResultsFetched = 'Search_Auto_Suggestion_Results_Fetched',
    SearchCapability = 'Search_Capability',
    SearchClick = 'Search_Click',
    SearchPageView = 'Search_PageView', // Used only when reporting current view for OCV events, kept for legacy reasons
    SearchPerformance = 'Search_Performance',
    SearchQuery = 'Search_Query',
    SearchRenderedResults = 'Search_Rendered_Results',
    SearchResultsFetched = 'Search_Results_Fetched',
    SearchSessionStarted = 'Search_Session_Started',
    SessionStorageUnavailable = 'Session_Storage_Unavailable',
    ShareControlAbandon = 'Share_Control_Abandon',
    ShareControlClick = 'Share_Control_Click',
    ShareControlError = 'Share_Control_Error',
    ShareControlImpression = 'Share_Control_Impression',
    ShareControlStarted = 'Share_Control_Started',
    SharedWithMeRequest = 'Shared_With_Me_Request',
    SharePointContextRequest = 'SharePoint_Context_Request',
    SharePointSiteClick = 'SharePoint_Site_Click',
    SharePointSitesImpression = 'SharePoint_Sites_Impression',
    SharePointSitesRequest = 'SharePoint_Sites_Request',
    ShellBootstrapperError = 'Shell_Bootstrapper_Load_Error',
    ShowMoreTilesImpression = 'ShowMoreTiles_Impression',
    SpaceGalleryPageView = 'SpaceGallery_PageView',
    SpacePageView = 'Space_PageView',
    SplitButtonToggle = 'Split_Button_Toggle',
    SuggestedActionClick = 'Suggested_Action_Click',
    // Start Service Worker events
    Sw_Activated = 'Sw_Activated',
    Sw_ApiMissingToken_Error = 'Sw_ApiMissingToken_Error',
    Sw_ApiMissingUserId_Error = 'Sw_ApiMissingUserId_Error',
    Sw_ApiNetworkRequest_Error = 'Sw_ApiNetworkRequest_Error',
    Sw_ApiRequestHandling_Error = 'Sw_ApiRequestHandling_Error',
    Sw_ApiTimeout_Error = 'Sw_ApiTimeout_Error',
    Sw_AppWarmupTimeout_Error = 'Sw_AppWarmupTimeout_Error',
    Sw_PrecacheAsset_Error = 'Sw_PrecacheAsset_Error',
    Sw_PrecacheCountMismatch_Error = 'Sw_PrecacheCountMismatch_Error',
    // End Service Worker events
    SwayCreateRequest = 'Sway_Create_Request',
    SwayCreateStoryAndPublishDocumentRequest = 'Sway_Create_Story_And_Publish_Document_Request',
    SwayGetAnalytics = 'Sway_Get_Analytics_Request',
    SwayGetThumbnailRequest = 'Sway_Get_Thumbnail_Request',
    TrendingAroundMeRequest = 'Trending_Around_Me_Request',
    TeamsAccessTokenRequest = 'Teams_Access_Token_Request',
    TellMeSearchSuggestionRequest = 'TellMe_Search_Suggestion_Request',
    TemplateClick = 'Template_Click',
    TemplateClickResult = 'Template_Click_Result',
    TemplateLibrariesRequest = 'Template_Libraries_Request',
    TemplatesCollectionClick = 'Templates_Collection_Click',
    TemplatesCollectionsRequest = 'Templates_Collections_Request',
    TemplatesRecommendedRequest = 'Templates_Recommended_Request',
    TemplatesGraphGetRequest = 'Templates_Graph_Get_Request',
    TemplatesErrorImpression = 'Templates_Error_Impression',
    TemplatesImpression = 'Templates_Impression',
    TemplatesPageView = 'Templates_PageView',
    TemplatesRequest = 'Templates_Request',
    Template_Content_Request = 'Template_Content_Request',
    TemplatesToggle = 'Templates_Toggle',
    ThumbnailLoad = 'Thumbnail_Load',
    ThumbnailCachePipeLoad = 'Thumbnail_Cache_Pipe_Load',
    TileToggleOpen = 'Tile_Toggle_Open',
    TileToggleSelect = 'Tile_Toggle_Select',
    TokensConfigMissing = 'Tokens_Config_Missing',
    UnexpectedHistoryState = 'Unexpected_History_State',
    UnhandledException = 'Unhandled_Exception',
    UnknownTheme = 'Unknown_Theme',
    UploadButtonImpression = 'Upload_Button_Impression',
    UploadClick = 'Upload_Click',
    UploadCompleted = 'Upload_Completed',
    UploadDialogImpression = 'Upload_Dialog_Impression',
    UploadLocalError = 'Upload_Local_Error',
    UpsellButtonClick = 'Upsell_Button_Click',
    UpsellButtonImpression = 'Upsell_Button_Impression',
    UpsellDialogImpression = 'Upsell_Dialog_Impression',
    UpsellDialogClose = 'Upsell_Dialog_Close',
    VisioLicenseStatus = 'Visio_License_Status',
    VroomActivityRequest = 'Vroom_Activity_Request',
    VroomCreateFileRequest = 'Vroom_Create_File_Request',
    VroomCreateLinkRequest = 'Vroom_Create_Link_Request',
    VroomCreateUploadSessionRequest = 'Vroom_Create_Upload_Session_Request',
    VroomGetFluidEnabled = 'Vroom_Get_Fluid_Enabled_Request',
    VroomItemRequest = 'Vroom_Item_Request',
    VroomItemWithWacDataRequest = 'Vroom_Item_With_Wac_Data_Request',
    VroomOneDriveCheckAccessRequest = 'Vroom_OneDrive_Item_Check_Access_Request',
    VroomSharesRequest = 'Vroom_Shares_Request',
    VroomSharesWithWacDataRequest = 'Vroom_Shares_With_Wac_Data_Request',
    VroomThumbnailStringRequest = 'Vroom_Thumbnail_String_Request',
    VroomUploadFileRequest = 'Vroom_Upload_File_Request',
    WacContentError = 'WacContent_Error',
    WacContentPageView = 'WacContent_PageView',
    WacDocumentAbandon = 'WacDocument_Abandon',
    WacDocumentBootstrapperLoadError = 'WacHost_Bootstrapper_Load_Error',
    WacDocumentGenericBootError = 'WacDocument_Generic_Boot_Error',
    WacDocumentJsApiError = 'WacDocument_JsApi_Error',
    WacDocumentJsApiTimeout = 'WacDocument_JsApi_Timeout',
    WacDocumentLoadStart = 'WacDocument_Load_Start',
    WacDocumentLoaded = 'WacDocument_Loaded',
    WacDocumentPreboot = 'WacDocument_Preboot',
    WacDocumentPreload = 'WacDocument_Preload',
    WacDocumentVroomError = 'WacDocument_Vroom_Error',
    UnifiedDocumentBootEnd = 'UnifiedDocumentBootEnd',
    UnifiedDocumentBootError = 'UnifiedDocumentBootError',
    UnifiedDocumentBootErrorExpected = 'UnifiedDocumentBootErrorExpected',
    WacPageAction = 'WacPageAction',
    WacStartPageView = 'WacStart_PageView',
    WamTokenRequestInitiated = 'WAM_Token_Request_Initiated',
    WamTokenRequest = 'WAM_Token_Request',
    WamTokenExpired = 'WAM_Token_Expired',
    WopiDataFetchFailed = 'Wopi_Data_Fetch_Failed',
    MoveToFolderControlError = 'Move_To_Folder_Control_Error',
    MoveToFolderControlClick = 'Move_To_Folder_Control_Click',
    MoveToFolderControlAbandon = 'Move_To_Folder_Control_Abandon',
    MoveToFolderControlImpression = 'Move_To_Folder_Control_Impression',
    MoveToFolderControlAuthenticate = 'Move_To_Folder_Control_Authenticate',
    MoveToFolderControlNavigationStarted = 'Move_To_Folder_Control_Navigation_Started',
    MoveToFolderControlNavigationEnded = 'Move_To_Folder_Control_Navigation_Ended',
    AdminPolicyServerRequest = 'Admin_Policy_Server_Request',
    AdminPolicyCacheRequest = 'Admin_Policy_Cache_Request'
}

/**
 * Standard actions.
 */
export const enum KnownInstrumentationActions {
    Click = 'Click',
    Expand = 'Expand',
    Collapse = 'Collapse',
    Close = 'Close',
    Cancel = 'Cancel',
    Open = 'Open',
    Focus = 'Focus',
    Submit = 'Submit'
}

/**
 * Platform of referral target (Ex. Word Online vs Word Win32)
 */
export const enum KnownReferralPlatform {
    Native = 'Native',
    Web = 'Web'
}

/**
 *  Intended destination for a given document click
 */
export const enum KnownClickDestinations {
    ContentPage = 'ContentPage',
    ExternalPage = 'ExternalPage'
}

/**
 * Standard referral types.
 * Firsttime: the first time we detect that a user has clicked on any element that refers them to a new workload.
 * Resurrected: A referral for someone who has not clicked on an app in 45 days or more. (45 days aligns with the most recent logic in office.com).
 * Repeat: Any referral that is not a first-time or resurrected referral.
 */
export class KnownReferralType {
    public static FirsttimeReferral = 'First time';
    public static ResurrectedReferral = 'Resurrected';
    public static RepeatReferral = 'Repeat';
    public static NotDetected = 'NotDetected';
}

/**
 * All UX areas, losely associated with sections on all
 */
export class KnownAreas {
    public static AllMyDocuments = 'AllMyDocuments';
    public static AppCategory = 'AppCategory';
    public static AppGalleryHero = 'AppGalleryHero';
    public static AppList = 'AppList';
    public static AppTiles = 'AppTiles';
    public static BackstageMru = 'BackstageMru';
    public static Banner = 'Banner';
    public static Calendar = 'Calendar';
    public static CreateCenter = 'CreateCenter';
    public static CreateFlyOut = 'CreateFlyOut';
    public static CreateGallery = 'CreateGallery';
    public static CreateModal = 'CreateModal';
    public static CreateOnHome = 'CreateOnHome';
    public static Documents = 'Documents';
    public static DocumentTitleComponent = 'DocumentTitleComponent';
    public static Edgeworth = 'Edgeworth';
    public static EnterpriseTemplates = 'EnterpriseTemplates';
    public static ErrorDialog = 'ErrorDialog';
    public static FeaturedDocs = 'FeaturedDocs';
    public static Feedback = 'Feedback';
    public static FirstRun = 'FirstRun';
    public static FluidContent = 'FluidContent';
    public static Folders = 'Folders';
    public static FormsGroups = 'FormsGroups';
    public static Hero = 'Hero';
    public static Incremental = 'Incremental';
    public static InputDialog = 'InputDialog';
    public static InstallWizard = 'InstallWizard';
    public static LeftNav = 'LeftNav';
    public static LeftNavCallout = 'LeftNavCallout';
    public static MobileAppBanner = 'MobileAppBanner';
    public static NewConsumerExperience = 'NewConsumerExperience';
    public static PeopleCard = 'PeopleCard';
    public static PwaDialog = 'PwaDialog';
    public static Recommended = 'Recommended';
    public static SearchResults = 'SearchResults';
    public static SearchSuggestions = 'SearchSuggestions';
    public static Shell = 'Shell';
    public static SideMenu = 'SideMenu';
    public static Sites = 'Sites';
    public static SuggestedActions = 'SuggestedActions';
    public static Templates = 'Templates';
    public static UploadDialog = 'UploadDialog';
    public static Upsell = 'Upsell';
}

/**
 * String containing personal identifiable information.
 */
export class PiiString {
    constructor(
        public value: string,
        public kind: AWTPiiKind = AWTPiiKind.GenericData
    ) {
    }
}

/**
 * Type alias for an object representing param/value pairs for instrumentation data recorded with instrumentation event.
 */
export type InstrumentationParams = { [param: string]: string | PiiString; };

/**
 * Interface defining Instrumentation event object.
 */
export interface InstrumentationEvent {
    /**
     * Name/value pairs that will be recorded with the event
     */
    params: InstrumentationParams;

    /**
     * List of metrics to increment when event is recorded.
     */
    metrics: Array<string>;

    /**
     * Call this method to record instrumentation event. All parameters defined in param
     * property will be recorded.
     */
    record(): void;
}

/**
 * Instrumentation service interface.
 */
@Injectable()
export abstract class InstrumentationService {

    // Detects both encoded and decoded email addresses. Copied from Shell team.
    private static readonly emailRegex = new RegExp('[a-zA-Z0-9_\\-\\.]+(@|%40|%2540)[a-zA-Z0-9_\\-\\.]+\\.[a-zA-Z]{2,63}', 'gm');

    // Tries to detect local file paths like 'file://user/path'. These often contain PII, so are scrubbed.
    private static readonly localFileRegex = new RegExp('file(:|%3A|%253A).*', 'gim');

    // Replaces the Search Query q paramter.
    private static readonly searchQueryRegex = new RegExp('([&?]q=)(.+?)(?=[&].*| |, |$)', 'gmi');

    // same logic is used on server in Utils.cs. Update that file too if making changes to these regexes.
    public static filterPii(log: string): string {
        if (!log) {
            return log;
        }

        // remove all new lines
        log.replace('\n', '').replace('\r', '').replace('\t', '').replace('\b', '');

        return log.replace(this.emailRegex, 'email_removed').replace(this.localFileRegex, 'file_removed').replace(this.searchQueryRegex, '$1query_removed');
    }

    /**
     * Logs InstrumentationEvent object with given type, name and set of params and metrics.
     *
     * @param eventName Instrumentation event name
     * @param params The object which maps instrumentation parameters with values.
     * @param metrics Array of metrics that have to be incremented as a result of recording generated event.
     * @param securityThreshold Security level associtated with the event.
     */
    public abstract logGenericEvent(
        eventName: KnownInstrumentationEvents,
        params?: InstrumentationParams,
        metrics?: Array<string>,
        securityThreshold?: DataSecurityLevel);

    /**
     * Logs InstrumentationEvent object for impression with given name and set of params and metrics.
     *
     * @param eventName Impression name
     * @param params The object which maps instrumentation parameters with values.
     * @param metrics Array of metrics that have to be incremented as a result of recording generated event.
     * @param securityThreshold Security level associtated with the event.
     */
    public abstract logImpressionEvent(
        eventName: KnownInstrumentationEvents,
        params?: InstrumentationParams,
        metrics?: Array<string>,
        securityThreshold?: DataSecurityLevel);

    /**
     * Logs InstrumentationEvent object for XHR requests with given name and set of params and metrics.
     *
     * @param eventName Name of the outgoing request.
     * @param httpCode httpcode of the response.
     * @param latency request latency.
     * @param error error messsage returned from the response if the code is not 200.
     * @param fromCache flag indicating if response came from Service Worker cache.
     * @param params The object which maps instrumentation parameters with values.
     * @param metrics Array of metrics that have to be incremented as a result of recording generated event.
     * @param securityThreshold Security level associtated with the event.
     */
    public abstract logRequestEvent(
        eventName: KnownInstrumentationEvents,
        httpCode: number,
        latency: number,
        error?: string | PiiString,
        fromCache?: boolean,
        params?: InstrumentationParams,
        metrics?: Array<string>,
        securityThreshold?: DataSecurityLevel);

    /**
     * Logs InstrumentationEvent object for Clicks with given name and set of params and metrics.
     *
     * @param eventName component source of the clicked link.
     * @param elementId element Id of the clicked link.
     * @param area UX area containing clicked link.
     * @param referral target workload of the clicked link.
     * @param params The object which maps instrumentation parameters with values.
     * @param metrics Array of metrics that have to be incremented as a result of recording generated event.
     * @param securityThreshold Security level associtated with the event.
     */
    public abstract logClickEvent(
        eventName: KnownInstrumentationEvents,
        elementId: string,
        area: string,
        referral?: string,
        referralPlatform?: KnownReferralPlatform,
        params?: InstrumentationParams,
        metrics?: Array<string>,
        securityThreshold?: DataSecurityLevel,
        isPageNavEvent?: boolean);

    /**
     * Logs InstrumentationEvent object for Errors with given name and set of params and metrics.
     *
     * @param eventName Instrumentation event name
     * @param params The object which maps instrumentation parameters with values.
     * @param metrics Array of metrics that have to be incremented as a result of recording generated event.
     * @param securityThreshold Security level associtated with the event.
     */
    public abstract logErrorEvent(
        eventName: KnownInstrumentationEvents,
        params?: InstrumentationParams,
        metrics?: Array<string>,
        securityThreshold?: DataSecurityLevel);

    /**
    * Logs InstrumentationEvent object for a performance measure with given name.
    *
    * @param eventName name to log under.
    * @param latency metrics to log.
    * @param params The object which maps instrumentation parameters with values.
    * @param securityThreshold Security level associtated with the event.
    */
    public abstract logPerformanceEvent(
        eventName: string,
        latency: number,
        params?: InstrumentationParams,
        securityThreshold?: DataSecurityLevel);

    /**
     * Logs InstrumentationEvent object for a debug cache with given name and set of params and metrics.
     *
     * @param eventName Instrumentation event name
     * @param params The object which maps instrumentation parameters with values.
     * @param metrics Array of metrics that have to be incremented as a result of recording generated event.
     * @param securityThreshold Security level associtated with the event.
     */
    public abstract logDebugCacheEvent(
        eventName: KnownInstrumentationEvents,
        params?: InstrumentationParams,
        metrics?: Array<string>,
        securityThreshold?: DataSecurityLevel);

    /**
     * Generates InstrumentationEvent object.
     *
     * @param eventName Instrumentation event name.
     * @param streamName Event stream name.
     * @param securityThreshold Security level associtated with the event.
     * @param params The object which maps instrumentation parameters with values.
     * @param metrics Array of metrics that have to be incremented as a result of recording generated event.
     * @param record Record the event immediately.
     * @returns InstrumentationEvent object which can be used to record the instrumentation
     * event with set of parameters specified in instrumentationData.
     */
    public abstract generateEvent(
        eventName: string,
        streamName: string,
        securityThreshold: DataSecurityLevel,
        params: InstrumentationParams,
        metrics?: Array<string>,
        record?: boolean
    ): InstrumentationEvent;

    /**
     * Generates InstrumentationEvent object for XHR requests with given name and set of params and metrics.
     *
     * @param eventName Name of the outgoing request.
     * @param httpCode httpcode of the response.
     * @param latency request latency.
     * @param error error messsage returned from the response if the code is not 200.
     * @param fromCache flag indicating if response came from Service Worker cache.
     * @param params The object which maps instrumentation parameters with values.
     * @param metrics Array of metrics that have to be incremented as a result of recording generated event.
     * @param securityThreshold Security level associtated with the event.
     */
    public abstract generateRequestEvent(
        eventName: KnownInstrumentationEvents,
        httpCode: number,
        latency: number,
        error?: string | PiiString,
        fromCache?: boolean,
        params?: InstrumentationParams,
        metrics?: Array<string>,
        securityThreshold?: DataSecurityLevel): InstrumentationEvent;

    /**
    * Sets log properies for global state values. This is called automatically for all logged events. Can be called
    * explicitly for events that are logged with delay.
    * @param params The object which maps instrumentation parameters with values.
    * @param overrideIfExist indicates if exisitng params with the names corresponding to global states should be overriden with current values.
    */
    public abstract applyGlobalStateParams(
        params: InstrumentationParams,
        overrideIfExist: boolean);

    /**
    * Flushes any buffered logs and sends them to the sinks, then closes the sinks.
    */
    public abstract flush();
}
